"use client";

import type { DailyCall } from "@daily-co/daily-js";
import {
  captureDailyDiagnostic,
  summarizeDailyPayload,
} from "./dailyjs-monitoring";

export interface DailyCallObjectTelemetryContext {
  attemptId?: string;
  pathname?: string | null;
  sessionId?: string | null;
  therapyRelationshipId?: string;
}

export type DailyCallObjectCleanupPhase =
  | "network-test-failed"
  | "network-test-restarted"
  | "network-test-unmounted"
  | "provider-unmounted"
  | "replaced-stale-instance";

const callObjectDestroyPromises = new WeakMap<DailyCall, Promise<boolean>>();

export function isDailyCallObjectDestroyed(callObject: DailyCall) {
  try {
    return callObject.isDestroyed();
  } catch {
    return false;
  }
}

export function destroyDailyCallObject(
  callObject: DailyCall,
  context: DailyCallObjectTelemetryContext,
  phase: DailyCallObjectCleanupPhase,
) {
  if (isDailyCallObjectDestroyed(callObject)) {
    return Promise.resolve(true);
  }

  const existingDestroy = callObjectDestroyPromises.get(callObject);
  if (existingDestroy) {
    return existingDestroy;
  }

  const destroyPromise = Promise.resolve()
    .then(() => callObject.destroy())
    .then(() => true)
    .catch((error: unknown) => {
      const destroyed = isDailyCallObjectDestroyed(callObject);

      captureDailyDiagnostic(
        "Daily call object cleanup failed",
        {
          action: "call-object-cleanup-failed",
          phase,
          error: summarizeDailyPayload(error),
          destroyed,
        },
        {
          ...context,
          event: "call-object-cleanup-failed",
        },
        destroyed ? "warning" : "error",
      );

      return destroyed;
    })
    .finally(() => {
      callObjectDestroyPromises.delete(callObject);
    });

  callObjectDestroyPromises.set(callObject, destroyPromise);
  return destroyPromise;
}
